import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Check,
  Copy,
  Database,
  Layers,
  RefreshCw,
  ScrollText,
  ShieldAlert,
  X,
  FileText,
} from "lucide-react";
import {
  rulesGetRule,
  guardrailsGetItem,
  knowledgeGetKnowledgeItem,
  knowledgeGetKnowledgeItemChunks,
  knowledgeGetKnowledgeChunk,
} from "../../gen";
import type {
  RuleResponse,
  GuardrailItemDetailResponse,
  KnowledgeItemResponse,
  KnowledgeChunkDetailResponse,
  KnowledgeChunkResponse,
} from "../../gen/models";
import { normalizeAgentType } from "../../hooks/useUserDirectory";

export type InspectionTarget =
  | { type: "rule"; id: string; initialData?: Partial<RuleResponse> }
  | { type: "guardrail"; id: string; initialData?: Partial<GuardrailItemDetailResponse> }
  | { type: "knowledge"; id: string; initialData?: Partial<KnowledgeItemResponse> }
  | { type: "chunk"; id: string; initialData?: Partial<KnowledgeChunkDetailResponse> };

interface GovernanceInspectionModalProps {
  target: InspectionTarget | null;
  onClose: () => void;
  onInspect?: (newTarget: InspectionTarget) => void;
}

export function GovernanceInspectionModal({
  target,
  onClose,
  onInspect,
}: GovernanceInspectionModalProps) {
  const navigate = useNavigate();

  // Data states
  const [ruleData, setRuleData] = useState<RuleResponse | null>(null);
  const [guardrailData, setGuardrailData] = useState<GuardrailItemDetailResponse | null>(null);
  const [knowledgeData, setKnowledgeData] = useState<KnowledgeItemResponse | null>(null);
  const [knowledgeChunks, setKnowledgeChunks] = useState<KnowledgeChunkResponse[]>([]);
  const [chunkData, setChunkData] = useState<KnowledgeChunkDetailResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!target) {
      setRuleData(null);
      setGuardrailData(null);
      setKnowledgeData(null);
      setKnowledgeChunks([]);
      setChunkData(null);
      setError(null);
      return;
    }

    let isCancelled = false;
    setLoading(true);
    setError(null);

    if (target.type === "rule") {
      if (target.initialData) {
        setRuleData(target.initialData as RuleResponse);
      }
      rulesGetRule(target.id)
        .then((res) => {
          if (!isCancelled) {
            setRuleData((res as any).data as RuleResponse);
          }
        })
        .catch((err: any) => {
          if (!isCancelled && !target.initialData) {
            setError(err?.message || "Failed to load rule details");
          }
        })
        .finally(() => {
          if (!isCancelled) setLoading(false);
        });
    } else if (target.type === "guardrail") {
      if (target.initialData) {
        setGuardrailData(target.initialData as GuardrailItemDetailResponse);
      }
      guardrailsGetItem(target.id)
        .then((res) => {
          if (!isCancelled) {
            setGuardrailData((res as any).data as GuardrailItemDetailResponse);
          }
        })
        .catch((err: any) => {
          if (!isCancelled && !target.initialData) {
            setError(err?.message || "Failed to load guardrail policy details");
          }
        })
        .finally(() => {
          if (!isCancelled) setLoading(false);
        });
    } else if (target.type === "knowledge") {
      if (target.initialData) {
        setKnowledgeData(target.initialData as KnowledgeItemResponse);
      }
      Promise.all([
        knowledgeGetKnowledgeItem(target.id),
        knowledgeGetKnowledgeItemChunks(target.id).catch(() => ({ data: [] })),
      ])
        .then(([itemRes, chunksRes]) => {
          if (!isCancelled) {
            setKnowledgeData((itemRes as any).data as KnowledgeItemResponse);
            setKnowledgeChunks(((chunksRes as any).data || []) as KnowledgeChunkResponse[]);
          }
        })
        .catch((err: any) => {
          if (!isCancelled && !target.initialData) {
            setError(err?.message || "Failed to load knowledge document details");
          }
        })
        .finally(() => {
          if (!isCancelled) setLoading(false);
        });
    } else if (target.type === "chunk") {
      if (target.initialData) {
        setChunkData(target.initialData as KnowledgeChunkDetailResponse);
      }
      knowledgeGetKnowledgeChunk(target.id)
        .then((res) => {
          if (!isCancelled) {
            setChunkData((res as any).data as KnowledgeChunkDetailResponse);
          }
        })
        .catch((err: any) => {
          if (!isCancelled && !target.initialData) {
            setError(err?.message || "Failed to load chunk details");
          }
        })
        .finally(() => {
          if (!isCancelled) setLoading(false);
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [target]);

  // Escape key handler
  useEffect(() => {
    if (!target) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [target, onClose]);

  if (!target) return null;

  const copyToClipboard = async (text: string, key: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleNavigateToDetails = () => {
    if (target.type === "rule") {
      navigate(`/rules/${target.id}`);
    } else if (target.type === "guardrail") {
      navigate(`/guardrails/${target.id}`);
    } else if (target.type === "knowledge") {
      navigate(`/knowledge/${target.id}`);
    } else if (target.type === "chunk") {
      navigate(`/knowledge/chunk/${target.id}`);
    }
    onClose();
  };

  const getPriorityBadge = (priority: number) => {
    if (priority >= 250) {
      return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-800/80";
    }
    if (priority >= 180) {
      return "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-400 dark:border-indigo-800/80";
    }
    return "bg-stone-100 text-stone-700 border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700";
  };

  const getStatusPill = (status?: string) => {
    switch (status) {
      case "active":
      case "published":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800/80";
      case "draft":
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80";
      case "superseded":
        return "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700";
      case "deprecated":
      case "archived":
        return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-800/80";
      default:
        return "bg-stone-100 text-stone-700 border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700";
    }
  };

  const renderGuardrailContent = () => {
    const guardrail = guardrailData;
    if (!guardrail && loading) {
      return (
        <div className="py-12 flex items-center justify-center gap-2 text-stone-400 text-xs">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading guardrail policy...</span>
        </div>
      );
    }
    if (!guardrail) {
      return (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Guardrail policy could not be found."}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Description / Policy */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-stone-700 dark:text-stone-300">
              Guardrail Policy & Boundaries
            </span>
            {guardrail.description && (
              <button
                type="button"
                onClick={() => copyToClipboard(guardrail.description || "", "guardrailDesc")}
                className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
              >
                {copiedKey === "guardrailDesc" ? (
                  <>
                    <Check className="w-3 h-3 text-emerald-500" />
                    <span className="text-emerald-500">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            )}
          </div>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950 p-4 text-xs font-mono leading-relaxed text-stone-800 dark:text-stone-200 whitespace-pre-wrap selection:bg-rose-100 dark:selection:bg-rose-950/60 max-h-48 overflow-y-auto">
            {guardrail.description || "No detailed description registered for this safety policy."}
          </div>
        </div>

        {/* Anchors preview if loaded */}
        {guardrail.anchors && guardrail.anchors.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-xs font-semibold text-stone-700 dark:text-stone-300">
              Vector Anchors ({guardrail.anchors.length})
            </span>
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {guardrail.anchors.slice(0, 10).map((a) => (
                <div
                  key={a.id}
                  className="p-2 rounded-lg bg-stone-100/70 dark:bg-stone-800/60 border border-stone-200 dark:border-stone-700/80 text-xs flex items-center justify-between gap-2"
                >
                  <span className="font-mono text-stone-700 dark:text-stone-300 truncate">
                    {a.text}
                  </span>
                  <span
                    className={`text-[9px] uppercase font-mono font-bold px-1.5 py-0.5 rounded shrink-0 ${
                      a.anchor_type === "negative"
                        ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400"
                        : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
                    }`}
                  >
                    {a.anchor_type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2 border-t border-stone-100 dark:border-stone-800/80 text-xs">
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Category</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200 capitalize">
              {guardrail.category}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Enforcement Action</span>
            <p className="font-mono font-semibold uppercase text-rose-600 dark:text-rose-400">
              {guardrail.action}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Severity</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200 capitalize">
              {guardrail.severity || "medium"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Default Threshold</span>
            <p className="font-mono text-stone-800 dark:text-stone-200">
              {guardrail.default_threshold ?? 0.86}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Guardrail ID</span>
            <div className="flex items-center gap-1 font-mono text-stone-600 dark:text-stone-400 truncate">
              <span className="truncate">{guardrail.id}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(guardrail.id, "guardrailId")}
                className="hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer shrink-0"
                title="Copy full Guardrail ID"
              >
                {copiedKey === "guardrailId" ? (
                  <Check className="w-2.5 h-2.5 text-emerald-500" />
                ) : (
                  <Copy className="w-2.5 h-2.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRuleContent = () => {
    const rule = ruleData;
    if (!rule && loading) {
      return (
        <div className="py-12 flex items-center justify-center gap-2 text-stone-400 text-xs">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading rule...</span>
        </div>
      );
    }
    if (!rule) {
      return (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Rule could not be found."}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Instruction Block */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-stone-700 dark:text-stone-300">
              Rule Instruction & Directive
            </span>
            <button
              type="button"
              onClick={() => copyToClipboard(rule.instruction || "", "instruction")}
              className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
            >
              {copiedKey === "instruction" ? (
                <>
                  <Check className="w-3 h-3 text-emerald-500" />
                  <span className="text-emerald-500">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950 p-4 text-xs font-mono leading-relaxed text-stone-800 dark:text-stone-200 whitespace-pre-wrap selection:bg-amber-100 dark:selection:bg-amber-950/60 max-h-72 overflow-y-auto">
            {rule.instruction}
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2 border-t border-stone-100 dark:border-stone-800/80 text-xs">
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Category</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200 capitalize">
              {rule.category}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Target Agent</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200">
              {rule.agent_type ? normalizeAgentType(rule.agent_type) : "Universal"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Priority</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200">
              {rule.priority}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Version</span>
            <p className="font-mono text-stone-800 dark:text-stone-200">
              v{rule.version}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Last Updated</span>
            <p className="text-stone-600 dark:text-stone-400 text-[11px]">
              {rule.updated_at ? new Date(rule.updated_at).toLocaleString() : "Unknown"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Rule ID</span>
            <div className="flex items-center gap-1 font-mono text-[11px] text-stone-500">
              <span>{rule.id.slice(0, 10)}...</span>
              <button
                type="button"
                onClick={() => copyToClipboard(rule.id, "ruleId")}
                className="hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
                title="Copy full Rule ID"
              >
                {copiedKey === "ruleId" ? (
                  <Check className="w-2.5 h-2.5 text-emerald-500" />
                ) : (
                  <Copy className="w-2.5 h-2.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKnowledgeContent = () => {
    const item = knowledgeData;
    if (!item && loading) {
      return (
        <div className="py-12 flex items-center justify-center gap-2 text-stone-400 text-xs">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading knowledge document...</span>
        </div>
      );
    }
    if (!item) {
      return (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Knowledge document could not be found."}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Document Content */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-stone-700 dark:text-stone-300">
              Document Text Content
            </span>
            <button
              type="button"
              onClick={() => copyToClipboard(item.content || "", "content")}
              className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
            >
              {copiedKey === "content" ? (
                <>
                  <Check className="w-3 h-3 text-emerald-500" />
                  <span className="text-emerald-500">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950 p-4 text-xs leading-relaxed text-stone-800 dark:text-stone-200 whitespace-pre-wrap selection:bg-indigo-100 dark:selection:bg-indigo-950/60 max-h-72 overflow-y-auto font-sans">
            {item.content}
          </div>
        </div>

        {/* Chunks List */}
        {knowledgeChunks.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <span className="text-[11px] font-semibold text-stone-700 dark:text-stone-300">
              Associated Chunks ({knowledgeChunks.length})
            </span>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {knowledgeChunks.map((chk) => (
                <button
                  key={chk.id}
                  type="button"
                  onClick={() => onInspect?.({ type: "chunk", id: chk.id, initialData: chk as any })}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-stone-100 dark:bg-stone-800 hover:bg-cyan-50 dark:hover:bg-cyan-950/50 hover:text-cyan-600 dark:hover:text-cyan-400 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700 text-[10px] font-mono transition cursor-pointer"
                  title={`Inspect chunk #${chk.chunk_index + 1}`}
                >
                  <Layers className="w-2.5 h-2.5 text-cyan-500" />
                  <span>#{chk.id.slice(0, 8)}</span>
                  <span className="text-stone-400 text-[9px]">({chk.char_length}c)</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2 border-t border-stone-100 dark:border-stone-800/80 text-xs">
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Category</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200 capitalize">
              {item.category}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Source Type</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200 uppercase">
              {item.source_type}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Total Chunks</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200">
              {item.chunk_count ?? knowledgeChunks.length}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Last Updated</span>
            <p className="text-stone-600 dark:text-stone-400 text-[11px]">
              {item.updated_at ? new Date(item.updated_at).toLocaleString() : "Unknown"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Target Agent</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200">
              {item.agent_type ? normalizeAgentType(item.agent_type) : "Universal"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Document ID</span>
            <div className="flex items-center gap-1 font-mono text-[11px] text-stone-500">
              <span>{item.id.slice(0, 10)}...</span>
              <button
                type="button"
                onClick={() => copyToClipboard(item.id, "itemId")}
                className="hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
                title="Copy full Document ID"
              >
                {copiedKey === "itemId" ? (
                  <Check className="w-2.5 h-2.5 text-emerald-500" />
                ) : (
                  <Copy className="w-2.5 h-2.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChunkContent = () => {
    const chunk = chunkData;
    if (!chunk && loading) {
      return (
        <div className="py-12 flex items-center justify-center gap-2 text-stone-400 text-xs">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading chunk...</span>
        </div>
      );
    }
    if (!chunk) {
      return (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Chunk could not be found."}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Parent Document Link / Notice */}
        {chunk.item_id && (
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-stone-100/70 dark:bg-stone-800/60 border border-stone-200 dark:border-stone-700/80 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <div className="min-w-0">
                <span className="text-[10px] text-stone-400 block font-medium">Source Document</span>
                <span className="font-semibold text-stone-800 dark:text-stone-200 truncate block">
                  {chunk.item_title || "Parent Document"}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onInspect?.({ type: "knowledge", id: chunk.item_id })}
              className="px-2.5 py-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded transition shrink-0 cursor-pointer"
            >
              Inspect Document
            </button>
          </div>
        )}

        {/* Chunk Text Content */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-stone-700 dark:text-stone-300">
              Chunk Segment Text
            </span>
            <button
              type="button"
              onClick={() => copyToClipboard(chunk.chunk_text || "", "chunkText")}
              className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
            >
              {copiedKey === "chunkText" ? (
                <>
                  <Check className="w-3 h-3 text-emerald-500" />
                  <span className="text-emerald-500">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950 p-4 text-xs font-mono leading-relaxed text-stone-800 dark:text-stone-200 whitespace-pre-wrap selection:bg-cyan-100 dark:selection:bg-cyan-950/60 max-h-72 overflow-y-auto">
            {chunk.chunk_text}
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2 border-t border-stone-100 dark:border-stone-800/80 text-xs">
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Chunk Index</span>
            <p className="font-mono font-semibold text-stone-800 dark:text-stone-200">
              Index #{chunk.chunk_index}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Characters</span>
            <p className="font-mono text-stone-800 dark:text-stone-200">
              {chunk.char_length} chars
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Embedding</span>
            <p className="font-semibold text-stone-800 dark:text-stone-200 flex items-center gap-1">
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  chunk.has_embedding ? "bg-emerald-500" : "bg-amber-500"
                }`}
              />
              <span>{chunk.has_embedding ? `${chunk.embedding_dim ?? 1536}d` : "Pending"}</span>
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Embedding Model</span>
            <p className="font-mono text-stone-600 dark:text-stone-400 text-[11px] truncate">
              {chunk.embedding_model || "text-embedding-3-small"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Created At</span>
            <p className="text-stone-600 dark:text-stone-400 text-[11px]">
              {chunk.created_at ? new Date(chunk.created_at).toLocaleString() : "Unknown"}
            </p>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-stone-400 font-medium">Chunk ID</span>
            <div className="flex items-center gap-1 font-mono text-[11px] text-stone-500">
              <span>{chunk.id.slice(0, 10)}...</span>
              <button
                type="button"
                onClick={() => copyToClipboard(chunk.id, "chunkId")}
                className="hover:text-stone-800 dark:hover:text-stone-200 transition cursor-pointer"
                title="Copy full Chunk ID"
              >
                {copiedKey === "chunkId" ? (
                  <Check className="w-2.5 h-2.5 text-emerald-500" />
                ) : (
                  <Copy className="w-2.5 h-2.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getHeaderIconAndTitle = () => {
    if (target.type === "rule") {
      return {
        icon: <ScrollText className="w-5 h-5 text-amber-500" />,
        badgeText: "Rule",
        badgeClass: "bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800",
        title: ruleData?.title || target.initialData?.title || "Rule Details",
        status: ruleData?.status,
        category: ruleData?.category || target.initialData?.category,
        priority: ruleData?.priority,
        goToDetailsText: "Go to Rule Details",
      };
    }
    if (target.type === "guardrail") {
      return {
        icon: <ShieldAlert className="w-5 h-5 text-rose-500" />,
        badgeText: "Guardrail Policy",
        badgeClass: "bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800",
        title: guardrailData?.title || target.initialData?.title || "Guardrail Details",
        status: guardrailData?.status || target.initialData?.status,
        category: guardrailData?.category || target.initialData?.category,
        priority: undefined,
        goToDetailsText: "Go to Guardrails Workspace",
      };
    }
    if (target.type === "knowledge") {
      return {
        icon: <BookOpen className="w-5 h-5 text-indigo-500" />,
        badgeText: "Knowledge Document",
        badgeClass: "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800",
        title: knowledgeData?.title || target.initialData?.title || "Knowledge Document Details",
        status: knowledgeData?.status,
        category: knowledgeData?.category || target.initialData?.category,
        priority: undefined,
        goToDetailsText: "Go to Knowledge Details",
      };
    }
    return {
      icon: <Layers className="w-5 h-5 text-cyan-500" />,
      badgeText: "Knowledge Chunk",
      badgeClass: "bg-cyan-50 dark:bg-cyan-950/60 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800",
      title:
        chunkData?.item_title ||
        target.initialData?.item_title ||
        `Chunk #${(chunkData?.chunk_index ?? 0) + 1}`,
      status: chunkData?.item_status,
      category: chunkData?.item_category,
      priority: undefined,
      goToDetailsText: "Go to Chunk Details",
    };
  };

  const header = getHeaderIconAndTitle();

  return (
    <div
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 dark:bg-black/75 backdrop-blur-xs p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[88vh] animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-start justify-between border-b border-stone-100 dark:border-stone-800 p-5 shrink-0 gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-xl bg-stone-100 dark:bg-stone-800 shrink-0 mt-0.5">
              {header.icon}
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${header.badgeClass}`}
                >
                  {header.badgeText}
                </span>

                {header.category && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700 capitalize">
                    {header.category}
                  </span>
                )}

                {header.priority != null && (
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${getPriorityBadge(
                      header.priority
                    )}`}
                  >
                    Priority {header.priority}
                  </span>
                )}

                {header.status && (
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold capitalize border ${getStatusPill(
                      header.status
                    )}`}
                  >
                    {header.status}
                  </span>
                )}
              </div>

              <h2 className="text-base font-bold text-stone-900 dark:text-stone-100 leading-snug truncate">
                {header.title}
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition shrink-0 cursor-pointer"
            title="Close dialog (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          {target.type === "rule" && renderRuleContent()}
          {target.type === "guardrail" && renderGuardrailContent()}
          {target.type === "knowledge" && renderKnowledgeContent()}
          {target.type === "chunk" && renderChunkContent()}
        </div>

        {/* Modal Footer Actions */}
        <div className="flex items-center justify-between border-t border-stone-100 dark:border-stone-800 px-5 py-3.5 bg-stone-50/50 dark:bg-stone-950/50 shrink-0">
          <div className="text-[11px] text-stone-400 flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" />
            <span className="font-mono">{target.id.slice(0, 16)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 font-medium transition cursor-pointer"
            >
              Close
            </button>

            <button
              type="button"
              onClick={handleNavigateToDetails}
              className="px-3.5 py-1.5 rounded-lg bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:hover:bg-white text-white dark:text-stone-900 font-semibold text-xs transition inline-flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <span>{header.goToDetailsText}</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
